import { Component, OnInit, Input } from '@angular/core';
import { CustomerService } from '../../customer.service';
import { forkJoin } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import * as _ from 'lodash';
import { CustomerLoansAssignmentDialogComponent } from './customer-loans-assignment-dialog/customer-loans-assignment-dialog.component';
import { CustomerLoanType, LoanDetails } from '../../customer.model';
import { UserPermissionService } from '../../../user/user-permission/user-permission.service';
import { readWrite } from '../../../user/user-permission/user-permission.data';
import { map } from 'rxjs/operators';
import { EnumHelper, LoanStatus, LoanInfoDto, SimpleDialogService, CommonActionKey } from 'common';

@Component({
    selector: 'ifb-customer-loans-widget',
    templateUrl: './customer-loans-widget.component.html',
    styleUrls: ['./customer-loans-widget.component.scss'],
    standalone: false
})
export class CustomerLoansWidgetComponent implements OnInit {
  @Input()
  edit: boolean;

  @Input()
  customerId: number;

  @Input()
  salesforceViewMode: boolean;

  loans: LoanDetails[];
  pendingLoans: LoanDetails[];

  canAddLoans: boolean;
  canAddPendingLoans: boolean;

  constructor(
    private readonly customerService: CustomerService,
    private readonly dialog: MatDialog,
    private readonly userPermissionService: UserPermissionService,
    private readonly simpleDialogService: SimpleDialogService
  ) { }

  ngOnInit(): void {
    this.refreshData();

    this.userPermissionService.granted([readWrite('servicing-customers-loans')])
      .subscribe(res => this.canAddLoans = res);

    this.userPermissionService.granted([readWrite('servicing-customers-pending-loans')])
      .subscribe(res => this.canAddPendingLoans = res);
  }

  private refreshData(): void {
    forkJoin([
      this.customerService.getLoansById(this.customerId),
      this.customerService.get(this.customerId)
    ]).pipe(
      map(results => ({
        loans: results[0] ? results[0].filter(l => results[1].loans.includes(l.id)) : [],
        pendingLoans: results[0] ? results[0].filter(l => results[1].pendingLoans.includes(l.id)) : []
      }))
    ).subscribe(res => {
      this.loans = res.loans;
      this.pendingLoans = res.pendingLoans;
    });
  }

  getStatus(statusId: number): string {
    return _.startCase(EnumHelper.getNameFromValue(LoanStatus, statusId));
  }

  getProgressColor(utilization: number): string {
    if (utilization < 60) {
      return "primary";
    }

    if (utilization < 90) {
      return "accent";
    }

    return "warn";
  }

  deleteLoan(loan: LoanDetails): void {
    const message = `Are you sure you want to remove account assignment <br>${loan.loanNumber} - ${loan.entityName}?`;
    this.simpleDialogService.openSimpleConfirmDialog(message, "Confirm")
    .subscribe((result: CommonActionKey) => {
      if (result === CommonActionKey.Confirm) {
        this.customerService.removeLoanById(this.customerId, loan.id)
          .subscribe(() => this.refreshData());
      }
    });
  }

  assignLoans(): void {
    CustomerLoansAssignmentDialogComponent.show(this.dialog, this.customerId, CustomerLoanType.Loans)
      .subscribe(data => {
        if (!data)
          return;
        this.customerService.assignLoans(this.customerId, data)
          .subscribe(() => this.refreshData());
      });
  }

  assignPendingLoans(): void {
    CustomerLoansAssignmentDialogComponent.show(this.dialog, this.customerId, CustomerLoanType.PendingLoans)
      .subscribe(data => {
        if (!data)
          return;
        this.customerService.assignPendingLoans(this.customerId, data)
          .subscribe(() => this.refreshData());
      });
  }

  accountDetailsLink(loan: LoanDetails): string {
    let path = `/account/${loan.id}/profile`;
    if (this.salesforceViewMode) {
      path = `/account/sf/${loan.uniqueId}/profile`;
    }
    return path;
  }

  getUtilization(loanInfo: LoanInfoDto): number {
    return Math.round((1 - loanInfo.availableFunds / loanInfo.creditLimit) * 100);
  }
}
